import {
  Routes,
  Route,
  Navigate,
  Outlet,
} from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import * as Sentry from '@sentry/react';
import { supabase } from './lib/supabase';
import {
  createOfflineQueryPersister,
  removeOfflineQueryCache,
} from './lib/offlineQueryPersister';
import {
  activateQueryCacheForUser,
  clearActiveQueryCache,
  QUERY_CACHE_MAX_AGE,
  queryClient,
} from './lib/queryClient';
import { AllCalendarsProvider } from './contexts/AllCalendarsContext';
import { CurrentCalendarProvider } from './contexts/CurrentCalendarContext';
import AppLayout from './components/AppLayout';
import LoginPage from './pages/LoginPage';
import AuthConfirmPage from './pages/AuthConfirmPage';
import CalendarRouter from './pages/CalendarPage';
import SettingsPage from './pages/SettingsPage';
import InAppBrowserOverlay from './components/InAppBrowserOverlay';
import { useInAppBrowser } from './hooks/useInAppBrowser';

function AuthenticatedLayout() {
  return (
    <AllCalendarsProvider>
      <CurrentCalendarProvider>
        <AppLayout>
          <Outlet />
        </AppLayout>
      </CurrentCalendarProvider>
    </AllCalendarsProvider>
  );
}

function setSentryUser(session: Session | null) {
  if (session?.user?.id) {
    Sentry.setUser({ id: session.user.id });
  } else {
    Sentry.setUser(null);
  }
}

export default function App() {
  const [session, setSession] = useState<
    Session | null | undefined
  >(undefined);
  const { isInAppBrowser, browserName, platform } =
    useInAppBrowser();
  const previousUserId = useRef<string | null>(null);

  useEffect(() => {
    if (isInAppBrowser) return;

    supabase.auth.getSession().then(({ data }) => {
      previousUserId.current =
        data.session?.user.id ?? null;
      setSession(data.session);
      setSentryUser(data.session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        const nextUserId = nextSession?.user.id ?? null;
        const priorUserId = previousUserId.current;
        if (priorUserId && priorUserId !== nextUserId) {
          void removeOfflineQueryCache(priorUserId).catch(
            () => undefined
          );
          clearActiveQueryCache();
        }
        previousUserId.current = nextUserId;
        setSession(nextSession);
        setSentryUser(nextSession);
      }
    );
    return () => subscription.unsubscribe();
  }, [isInAppBrowser]);

  if (isInAppBrowser) {
    return (
      <InAppBrowserOverlay
        browserName={browserName}
        platform={platform}
      />
    );
  }

  if (session === undefined) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-paper-100">
        <span className="font-mono text-ink-400 text-sm">
          Loading…
        </span>
      </div>
    );
  }

  if (!session) {
    return (
      <Routes>
        <Route
          path="/auth/confirm"
          element={<AuthConfirmPage />}
        />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  activateQueryCacheForUser(session.user.id);

  return (
    <PersistQueryClientProvider
      key={session.user.id}
      client={queryClient}
      persistOptions={{
        persister: createOfflineQueryPersister(
          session.user.id
        ),
        maxAge: QUERY_CACHE_MAX_AGE,
        buster:
          import.meta.env.VITE_BUILD_ID ?? 'offline-v1',
        dehydrateOptions: {
          shouldDehydrateQuery: query => {
            const key = query.queryKey[0];
            if (key === 'events') {
              return query.queryKey[4] == null;
            }
            return (
              typeof key === 'string' &&
              [
                'me',
                'calendars',
                'subscriptions',
                'members',
                'holidays',
              ].includes(key)
            );
          },
        },
      }}
    >
      <Routes>
        <Route path="/" element={<AuthenticatedLayout />}>
          <Route
            index
            element={<Navigate to="/calendar" replace />}
          />
          <Route
            path="/calendar"
            element={<CalendarRouter />}
          />
          <Route
            path="/calendar/:calendarId"
            element={<CalendarRouter />}
          />
          <Route
            path="/settings"
            element={<SettingsPage />}
          />
          <Route
            path="*"
            element={<Navigate to="/calendar" replace />}
          />
        </Route>
      </Routes>
    </PersistQueryClientProvider>
  );
}
